import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';

const SearchableMultiSelect = ({
    options = [],
    selectedValues = [],
    onChange,
    placeholder = "Rechercher et sélectionner...",
    searchPlaceholder = "Rechercher...",
    loading = false,
    disabled = false,
    maxHeight = "200px",
    showOptionMeta = true,
    selectedCountLabel = null,
    getOptionLabel = null,
    getOptionMeta = null,
    triggerClassName = "px-4 py-3",
    textClassName = "",
}) => {
    const labelOf = (option) => (getOptionLabel ? getOptionLabel(option) : option.title);
    const metaOf = (option) => {
        if (getOptionMeta) return getOptionMeta(option);
        if (option.limesurvey_id != null || option.price_per_complete != null) {
            return `ID: ${option.limesurvey_id}${option.price_per_complete != null ? ` - ${option.price_per_complete} MAD/complétion` : ''}`;
        }
        return null;
    };
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Filter options based on search term
    const filteredOptions = options.filter(option => {
        const s = searchTerm.toLowerCase();
        const label = labelOf(option);
        const meta = metaOf(option);
        return (
            (label && String(label).toLowerCase().includes(s)) ||
            (meta && String(meta).toLowerCase().includes(s)) ||
            (option.limesurvey_id != null && String(option.limesurvey_id).includes(searchTerm))
        );
    });

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const handleToggle = () => {
        if (!disabled && !loading) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        }
    };

    const handleOptionToggle = (option) => {
        const isSelected = selectedValues.includes(option.id);
        if (isSelected) {
            onChange(selectedValues.filter(id => id !== option.id));
        } else {
            onChange([...selectedValues, option.id]);
        }
    };

    const handleKeyDown = (e) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleOptionToggle(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
                break;
        }
    };

    const getSelectedOptions = () => {
        return options.filter(option => selectedValues.includes(option.id));
    };

    const removeSelected = (optionId) => {
        onChange(selectedValues.filter(id => id !== optionId));
    };

    const selectedOptions = getSelectedOptions();

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Input Field */}
            <div
                className={`w-full ${triggerClassName} ${textClassName} border border-gray-300 rounded-lg cursor-pointer transition-colors duration-200 ${
                    isOpen
                        ? 'ring-2 ring-red-500 border-red-500'
                        : 'hover:border-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                onClick={handleToggle}
            >
                <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                        {selectedOptions.length === 0 ? (
                            <span className="text-gray-500">{placeholder}</span>
                        ) : (
                            <div className="flex flex-wrap gap-1">
                                {selectedOptions.map((option) => (
                                    <span
                                        key={option.id}
                                        className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 text-xs rounded-md"
                                    >
                                        {labelOf(option)}
                                        {!disabled && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeSelected(option.id);
                                                }}
                                                className="ml-1 hover:text-red-600"
                                            >
                                                <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                                            </button>
                                        )}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                        <FontAwesomeIcon 
                            icon={faChevronDown} 
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                            }`}
                        />
                    </div>
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                            <FontAwesomeIcon 
                                icon={faSearch} 
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                            />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={searchPlaceholder}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                            />
                        </div>
                    </div>

                    {/* Options List */}
                    <div 
                        className="overflow-y-auto"
                        style={{ maxHeight }}
                    >
                        {loading ? (
                            <div className="p-4 text-center text-gray-500">
                                <FontAwesomeIcon icon={faSearch} spin className="mr-2" />
                                Chargement...
                            </div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                                {searchTerm ? 'Aucun résultat trouvé' : 'Aucune option disponible'}
                            </div>
                        ) : (
                            filteredOptions.map((option, index) => {
                                const isSelected = selectedValues.includes(option.id);
                                const isHighlighted = index === highlightedIndex;
                                
                                return (
                                    <div
                                        key={option.id}
                                        className={`px-4 py-3 cursor-pointer transition-colors duration-150 ${
                                            isHighlighted 
                                                ? 'bg-red-50' 
                                                : 'hover:bg-gray-50'
                                        } ${isSelected ? 'bg-red-50' : ''}`}
                                        onClick={() => handleOptionToggle(option)}
                                    >
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0 mt-1">
                                                {isSelected ? (
                                                    <FontAwesomeIcon 
                                                        icon={faCheck} 
                                                        className="w-4 h-4 text-red-600" 
                                                    />
                                                ) : (
                                                    <div className="w-4 h-4 border border-gray-300 rounded"></div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-gray-900">
                                                    {labelOf(option)}
                                                </div>
                                                {showOptionMeta && metaOf(option) && (
                                                    <div className="text-sm text-gray-500">
                                                        {metaOf(option)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {selectedOptions.length > 0 && (
                        <div className="p-3 border-t border-gray-200 bg-gray-50">
                            <div className="text-sm text-gray-600">
                                {selectedCountLabel != null
                                    ? selectedCountLabel(selectedOptions.length)
                                    : `${selectedOptions.length} enquête${selectedOptions.length > 1 ? 's' : ''} sélectionnée${selectedOptions.length > 1 ? 's' : ''}`}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableMultiSelect;
